import { useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useParams } from 'react-router';

import { Avatar, Box, Button, Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';

import { DocumentEditorContainerComponent, CustomToolbarItemModel, ToolbarItem, Toolbar } from '@syncfusion/ej2-react-documenteditor';
import { DropDownButton, ItemModel } from '@syncfusion/ej2-react-splitbuttons';

import moment from 'moment';

import { TabPanel, TabProps } from '../../../helpers/TabPanel';
import { DefaultDocumentEditor } from '../../../helpers/DefaultDocumentEditor';

import { openSnackbar } from '../../../store/slices/SnackbarSlice';

import { DialogActionsStyle, DialogContentStyle } from '../../../styles/DialogStyle';

import { Layout } from '../../../components/Layout';
import { InfoField } from '../../../components/InfoField';
import { BoxProcesoCancelado } from '../../../components/BoxProcesoCancelado';
import { BoxProcesoTerminado } from '../../../components/BoxProcesoTerminado';
import { BoxHeaderInformacion } from '../../../components/BoxHeaderInformacion';
import { BoxProcesoNoEncontrado } from '../../../components/BoxProcesoNoEncontrado';
import { BoxInformacionExpediente } from '../../../components/BoxInformacionExpediente';
import { CircularProgressIndicator } from '../../../components/CircularProgressIndicator';

import { ActualizarEstatusSentenciaPublica, GuardarSentenciaPublica, DetalleSentenciaPublica } from '../../../connections/sentencia/SentenciaPublicaConnection';

import { UsuarioObservacionesProps } from '../../../interfaces/sentencia/SentenciaInterface';
import { ActualizarSentenciaPublicaParams, defaultSentenciaPublica, SentenciaPublicaProps } from '../../../interfaces/sentencia/SentenciaPublicaInterface';

DocumentEditorContainerComponent.Inject(Toolbar);

interface ItemModelModif extends ItemModel {
    value?: string;
}

export const ElaborarSentenciaPublicaPage = () => {

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const { Id } = useParams();

    const syncfusionEditor = useRef<DocumentEditorContainerComponent | null>( null );
    const contenidoRef = useRef( '' );

    const dropCampos = useRef<DropDownButton>( null );

    const [value, setValue] = useState( 0 );

    const [sentenciaPublica, setSentenciaPublica] = useState<SentenciaPublicaProps>( defaultSentenciaPublica );

    const [observaciones, setObservaciones] = useState<UsuarioObservacionesProps[]>( [] );

    const [procesoTerminado, setProcesoTerminado] = useState( false );
    const [procesoCancelado, setProcesoCancelado] = useState( false );
    const [procesoNoEncontrado, setProcesoNoEncontrado] = useState( false );
    const [mensajeFinalizacion, setMensajeFinalizacion] = useState( '' );
    const [mensajeAsignacion, setMensajeAsignacion] = useState( '' );

    const [loading, setLoading] = useState( true );
    const [loadingFinalizar, setLoadingFinalizar] = useState( false );
    const [loadingGuardar, setLoadingGuardar] = useState( false );

    const [openDialogConfirmacionFinalizar, setOpenDialogConfirmacionFinalizar] = useState( false );

    let Toolbar: (CustomToolbarItemModel | ToolbarItem)[] = [
        'New',
        'Open',
        'Separator',
        'Undo',
        'Redo',
        'Separator',
        'Image',
        'Table',
        'Separator',
        'Header',
        'Footer',
        'Separator',
        {
            tooltipText: 'Atajos',
            text: 'Atajos',
            id: 'dropItem',
            prefixIcon: 'e-icons e-drop-down',
        }
    ];

    const handleChange = ( _: React.SyntheticEvent, newValue: number ) => {

        if ( value === 0 && newValue !== 0 ) {

            const serialized = syncfusionEditor.current?.documentEditor.serialize() ?? '';

            contenidoRef.current = serialized;
        }

        setValue( newValue );
    }

    const Guardar = async () => {

        const params: ActualizarSentenciaPublicaParams = {
            idSentenciaPublica: Id ?? '',
            contenido: syncfusionEditor?.current?.documentEditor.serialize() ?? '',
        }

        return await GuardarSentenciaPublica( params );
    }

    const handleGuardar = async () => {

        setLoadingGuardar( true );

        await Guardar().then(resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if ( success ) {

                    dispatch( openSnackbar({ message: message, variant: 'success' }) );
                    setLoadingGuardar( false );

                }
                else {

                    dispatch( openSnackbar({ message: message, variant: 'error' }) );
                    setLoadingGuardar( false );

                }

            }, 700);

        });

    }

    const handleFinalizar = async () => {

        setLoadingFinalizar( true );

        const { success: successGuardar, message: messageGuardar } = await Guardar();

        if ( !successGuardar ) {

            setTimeout(() => {

                dispatch( openSnackbar({ message: messageGuardar, variant: 'error' }) );
                setLoadingFinalizar( false );

            }, 700);

            return;
        }

        await ActualizarEstatusSentenciaPublica({ idSentenciaPublica: Id ?? '', idEstatus: 41, observaciones: '' }).then(resp => {

            setTimeout(() => {

                const { success, message, asignadoMismoUsuario, usuarioAsignado } = resp;

                if ( success ) {

                    if ( asignadoMismoUsuario ) {

                        navigate(`/sentencia-publica/autorizar/${ Id ?? '' }`);

                    }
                    else {

                        setProcesoTerminado( true );
                        setMensajeAsignacion(`La siguiente actividad se asignó a ${ usuarioAsignado }`);

                    }

                    setLoadingFinalizar( false );

                }
                else {

                    dispatch(openSnackbar({ message: message, variant: 'error' }));
                    setLoadingFinalizar( false );

                }

            }, 700);

        });

    }

    //--------------------------------------------- funciones para el editor de text --------------------------------------------------

    const configurarEditor = ( contenido?: string ) => {

        if ( !syncfusionEditor.current ) return;

        syncfusionEditor.current.documentEditor.open( contenido ?? DefaultDocumentEditor() );
        syncfusionEditor.current.documentEditorSettings.showRuler = true;
        syncfusionEditor.current.documentEditor.pageOutline = '#D0D0D0';

        dropCampos.current?.appendTo('#dropItem');
    };

    useEffect(() => {

        if ( !loading ) {

            configurarEditor( contenidoRef.current || undefined );

        }

    }, [ loading ]);

    useEffect(() => {

        if ( value === 0 && !loading && syncfusionEditor.current ) {

            if ( contenidoRef.current ) {
                syncfusionEditor.current.documentEditor.open( contenidoRef.current );
            }

        }

    }, [ value, loading ]);

    //----------------------------------------------------------------------------------------------------------------------------------

    useEffect(() => {

        setLoading( true );

        async function Obtener() {

            await DetalleSentenciaPublica( Id ?? '' ).then(resp => {

                setTimeout(() => {

                    const { success } = resp;

                    if ( success ) {

                        setSentenciaPublica( resp );
                        setObservaciones( resp.usuarioObservaciones );

                        contenidoRef.current = resp.contenido;

                        const mkItem = (text: string, id: string, value: string): ItemModelModif[] =>
                            [{ text, id, iconCss: 'e-icons e-bookmark', value }, { separator: true }];
                        const joinNames = (arr: string[]) => arr.join(', ');
                        const fmtDate = (d: any) => moment(d).format('DD-MM-YYYY');

                        let items: ItemModelModif[] = [
                            ...mkItem('Fecha Actual', '1', '[Fecha_Actual]'),
                            ...mkItem('Numero de sentencia', '2', resp.numeroSentencia),
                        ];

                        if ( resp.idExpediente !== 0 ) {
                            const { expediente: e } = resp;
                            items.push(
                                ...mkItem('Número de Expediente', '4', e.numeroExpediente),
                                ...mkItem('Tipo de Juicio', '6', e.tipoJuicio),
                                ...mkItem('Juzgado Origen', '10', e.juzgadoOrigen),
                                ...mkItem('Fecha del Expediente', '11', fmtDate(e.fecha)),
                                ...mkItem('Actor(es)', '12', joinNames( e.actores )),
                                ...mkItem('Demandado(s)', '13', joinNames( e.demandados )),
                                ...mkItem('Juez', '13', e.juez),
                            );
                            if ( e.numeroFolio )            items.push(...mkItem('Número de Folio', '14', e.numeroFolio));
                            if ( e.secretarioTramite )      items.push(...mkItem('Secretario de Tramite', '5', e.secretarioTramite));
                            if ( e.secretarioTramiteMenor ) items.push(...mkItem('Secretario de Tramite Menor', '7', e.secretarioTramiteMenor));
                            if ( e.secretarioEjecucion )    items.push(...mkItem('Secretario de Ejecucion', '15', e.secretarioEjecucion));
                        }

                        if ( items[items.length - 1]?.separator ) { items.pop(); }

                        dropCampos.current = new DropDownButton({
                            items: items,
                            beforeItemRender: (data: ItemModelModif) => {
                                return `<div>
                                                <span class="e-menu-icon ${data.iconCss}"></span>
                                                <span class="custom-class" >${data.text}</span>
                                            </div>`;
                            },
                            select: (args) => syncfusionEditor.current?.documentEditor.editor.insertText( args?.item.value ?? "" ),
                        },);

                        if ( resp.idEstatus === 3 ) {
                            setMensajeFinalizacion( 'No se tienen actividades pendientes para este trámite' );
                            setProcesoTerminado( true );
                            setLoading( false );
                        }
                        else if ( resp.idEstatus === 4 ) {
                            setProcesoCancelado( true );
                            setLoading( false );
                        }
                        else if ( !resp.asignadoMismoUsuario ) {
                            setMensajeAsignacion(`La siguiente actividad se asignó a ${ resp.nombreUsuarioAsignado }`);
                            setProcesoTerminado( true );
                            setLoading( false );
                        }
                        else if ( resp.asignadoMismoUsuario && resp.idEstatus !== 40 ) {
                            setMensajeAsignacion( 'La siguiente actividad se encuentra en tu bandeja' );
                            setProcesoTerminado( true );
                            setLoading( false );
                        }
                        else if ( resp.asignadoMismoUsuario && resp.idEstatus === 40 ) {
                            setLoading( false );
                        }
                    }
                    else {

                        setProcesoNoEncontrado( true );
                        setLoading( false );

                    }

                }, 700);

            });

        }

        if ( Id ) {
            Obtener();
        }

    }, [ Id ])

    return (
        <Layout breadcrumbs={ [{ text: 'Sentencia pública' }, { text: 'Elaborar sentencia pública' }] } backgroundPaper={ ( procesoTerminado || procesoCancelado || procesoNoEncontrado ) }>

            {
                loading
                ?
                    <Box component={ Paper } height={ '70vh' } textAlign={ 'center' } alignContent={ 'center' } alignItems={ 'center' }>
                        <CircularProgressIndicator />
                    </Box>
                :
                    ( procesoTerminado || procesoCancelado || procesoNoEncontrado )
                    ?
                        procesoNoEncontrado
                        ?
                            <BoxProcesoNoEncontrado />
                        :
                            procesoCancelado
                            ?
                                <BoxProcesoCancelado />
                            :
                                <BoxProcesoTerminado message={ mensajeFinalizacion } messageAssing={ mensajeAsignacion } processEnd={ sentenciaPublica.idEstatus === 3 } />
                        :
                            <>

                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>

                                    <Tabs value={ value } onChange={ handleChange }>

                                        <Tab label='Sentencia pública' {...TabProps(0)} />
                                        <Tab label="Información del expediente" {...TabProps(1)} />

                                    </Tabs>

                                </Box>

                                <div role="tabpanel" hidden={ value !== 0 } id="vertical-tabpanel-0" aria-labelledby="vertical-tab-0" style={{ padding: '16px 0' }}>

                                    <Box p={ 4 } component={ Paper }>

                                        <BoxHeaderInformacion
                                            numeroSeguimiento={ `GSEP-${ sentenciaPublica.idSentenciaPublica }` }
                                            fecha={ sentenciaPublica.fecha ?? new Date() }
                                            juez={ sentenciaPublica.juez }
                                        />

                                        <Grid container spacing={ 3 } mt={ 2 }>

                                            <Grid size={{ md: 4, xs: 12 }}>

                                                <InfoField
                                                    chip
                                                    label="Número de Sentencia (Resolución)"
                                                    value={ sentenciaPublica.numeroSentencia }
                                                />

                                            </Grid>

                                            <Grid size={{ md: 4, xs: 12 }}>

                                                <InfoField
                                                    chip
                                                    label="Tipo de Sentencia"
                                                    value={ `Sentencia ${ sentenciaPublica.tipoSentencia }` }
                                                />

                                            </Grid>

                                            <Grid size={{ md: 4, xs: 12 }}>

                                                <InfoField
                                                    chip
                                                    label="Fecha de Sentencia"
                                                    value={ sentenciaPublica.fechaSentencia ? moment( sentenciaPublica.fechaSentencia ).format('DD-MM-YYYY hh:mm a') : '' }
                                                />

                                            </Grid>

                                            <Grid size={{ md: 4, xs: 12 }}>

                                                <InfoField
                                                    chip
                                                    label="Sentido de sentencia"
                                                    value={ sentenciaPublica.sentidoSentencia }
                                                />

                                            </Grid>

                                            <Grid size={{ md: 4, xs: 12 }}>

                                                <InfoField
                                                    chip
                                                    label="¿Con perspectiva género?"
                                                    value={ sentenciaPublica.perspectivaGenero ? 'Si' : 'No' }
                                                />

                                            </Grid>

                                            <Grid size={{ md: 12, xs: 12 }}>

                                                <DocumentEditorContainerComponent
                                                    key='DocumentEditorContainer'
                                                    serviceUrl={ process.env.REACT_APP_URL_SERVICE_DOCUMENT_EDITOR ?? '' }
                                                    height='650'
                                                    ref={ syncfusionEditor }
                                                    enableToolbar={ true }
                                                    showPropertiesPane={ false }
                                                    toolbarItems={ Toolbar }
                                                    autoResizeOnVisibilityChange={ false }
                                                >
                                                </DocumentEditorContainerComponent>

                                            </Grid>

                                            {
                                                sentenciaPublica.observaciones
                                                &&
                                                    <Grid size={{ xs: 12, md: 12 }}>

                                                        <Card sx={{ p: 2 }}>

                                                            <Typography variant='h6' sx={{ mb: 2 }}>
                                                                Observaciones
                                                            </Typography>

                                                            <Stack spacing={ 2 }>

                                                                {
                                                                    observaciones.map( ( { usuario, puesto, observacion, fecha }, i ) => (

                                                                        <Box key={ i } sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>

                                                                            <Avatar
                                                                                sx={{
                                                                                    width: 36,
                                                                                    height: 36,
                                                                                    fontSize: 12,
                                                                                    fontWeight: 500,
                                                                                    bgcolor: (theme) => theme.palette.primary.main,
                                                                                    color: 'white',
                                                                                }}
                                                                            >
                                                                                { usuario.split(' ').slice(0, 2).map(n => n[0]).join('') }
                                                                            </Avatar>

                                                                            <Box
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    bgcolor: 'action.hover',
                                                                                    border: '0.5px solid',
                                                                                    borderColor: 'divider',
                                                                                    borderRadius: '0 15px 15px 15px',
                                                                                    px: 2,
                                                                                    py: 1.25,
                                                                                }}
                                                                            >
                                                                                <Typography variant='subtitle2' fontWeight={ 500 }>
                                                                                    { usuario } { puesto ? `(${ puesto })` : '' }
                                                                                </Typography>

                                                                                <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5, lineHeight: 1.5 }}>
                                                                                    { observacion }
                                                                                </Typography>

                                                                                {
                                                                                    fecha
                                                                                    &&
                                                                                        <Typography variant='caption' color='text.disabled' sx={{ mt: 0.75, display: 'block' }}>
                                                                                            { moment( fecha ).format('DD-MM-YYYY hh:mm a') }
                                                                                        </Typography>
                                                                                }

                                                                            </Box>

                                                                        </Box>

                                                                    ))
                                                                }

                                                            </Stack>

                                                        </Card>

                                                    </Grid>
                                            }

                                            <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>

                                                <Grid container spacing={ 3 }>

                                                    <Grid size={{ md: 6, xs: 12 }} sx={{ textAlign: 'right' }}>

                                                        <Button
                                                            fullWidth
                                                            color='secondary'
                                                            variant='outlined'
                                                            loading={ loadingGuardar }
                                                            onClick={ handleGuardar }
                                                        >
                                                            Guardar
                                                        </Button>

                                                    </Grid>

                                                    <Grid size={{ md: 6, xs: 12 }} style={{ textAlign: 'left' }}>
                                                        <Button
                                                            fullWidth
                                                            color='primary'
                                                            variant='contained'
                                                            onClick={ () => setOpenDialogConfirmacionFinalizar( true ) }
                                                        >
                                                            Finalizar
                                                        </Button>
                                                    </Grid>

                                                </Grid>

                                            </Grid>

                                        </Grid>

                                    </Box>

                                </div>

                                <TabPanel value={ value } index={ 1 }>

                                    <BoxInformacionExpediente
                                        expediente={ sentenciaPublica.expediente }
                                    />

                                </TabPanel>

                                <Dialog
                                    fullWidth
                                    maxWidth='xs'
                                    open={ openDialogConfirmacionFinalizar }
                                    onClose={ () => { } }
                                >

                                    <DialogTitle>
                                        Confirmación
                                    </DialogTitle>

                                    <DialogContent sx={ DialogContentStyle }>

                                        <DialogContentText>
                                            ¿Desea finalizar la elaboración de la sentencia?
                                        </DialogContentText>

                                    </DialogContent>

                                    <DialogActions sx={ DialogActionsStyle }>
                                        <Button color="secondary" disabled={ loadingFinalizar } onClick={ () => setOpenDialogConfirmacionFinalizar( false ) }> Cancelar </Button>
                                        <Button loading={ loadingFinalizar } onClick={ handleFinalizar } variant='contained' > Aceptar </Button>
                                    </DialogActions>

                                </Dialog>

                            </>
            }

        </Layout>
    )
}
